import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CWS_URL } from "@/components/SiteChrome";

const extensionPresent = () => !!document.querySelector("#glosslink-floating-icon, #glosslink-overlay, .glosslink-tooltip, #glosslink-selection-popup");

const waitForExtensionReply = (ms = 10000) => new Promise((resolve) => {
  const started = Date.now();
  const tick = () => {
    const pop = document.getElementById("glosslink-selection-popup");
    const text = pop ? pop.innerText.replace(/\s+/g, " ").trim() : "";
    if (/imported|added|free plan/i.test(text)) return resolve(text.replace(/^GlossLink\s*×?\s*/i, ""));
    if (Date.now() - started > ms) return resolve(null);
    setTimeout(tick, 150);
  };
  tick();
});

export const useAddToGlossLink = (pack) => {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/packs/${pack.id}/csv`);
      window.postMessage({ type: "glosslink-import", csv: data.csv, name: data.name }, "*");
      setSent(true);
      const reply = await waitForExtensionReply();
      const status = reply ? "confirmed" : extensionPresent() ? "unknown" : "noext";
      window.dispatchEvent(new CustomEvent("glosspack-added", { detail: { status, extMessage: reply || "", name: data.name, csv: data.csv } }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { sent, busy, add };
};

export const AddButton = ({ pack, sent, busy, add, size = "md", testId }) => (
  <button onClick={add} disabled={busy} data-testid={testId}
    className={`${size === "lg" ? "w-full py-4 text-lg rounded-xl" : "px-5 py-2.5 text-sm rounded-lg"} font-bold text-white shadow-lg shadow-blue-600/20 transition-colors disabled:opacity-60 ${sent ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}>
    {busy ? "Adding… waiting for GlossLink" : sent ? "✓ Sent to GlossLink" : "Add to my GlossLink →"}
  </button>
);

export const AfterAddHelp = ({ pack, sent }) => {
  if (!sent) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-slate-700" data-testid="pack-add-help">
      <p><b>Look for the GlossLink confirmation popup</b> in the corner of this page — your terms are now highlighted on every site you read.</p>
      <p className="mt-2">The popup tells you whether you're in your 20-day free trial (everything unlocked) or on the free plan (20 most-used terms defined, the rest in grey). Upgrade to Pro in the GlossLink popup ($9.99/mo or $75/yr) and every term in every Gloss Pack unlocks instantly.</p>
      <p className="mt-3"><b>No popup appeared?</b> Install GlossLink first, then come back and click the button again.</p>
      <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="mt-1 block font-semibold text-blue-600 underline" data-testid="pack-add-install-link">Install GlossLink from the Chrome Web Store →</a>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/packs/${pack.id}/csv?download=1`} className="mt-2 block text-xs text-slate-500 underline" data-testid="pack-add-download-csv">Or download the CSV and upload it in the GlossLink popup</a>
    </div>
  );
};

export const PlanNote = ({ pack }) => (
  <div className="space-y-2 text-xs text-slate-600" data-testid="pack-plan-note">
    <p><b>First 20 days — everything free:</b> your first word or pack starts a 20-day trial with the whole pack unlocked. No card needed.</p>
    <p><b>After that, free forever:</b> 20 of this pack's most-used terms stay fully defined, the rest show in grey so you can see what Pro unlocks — plus quizzes and flashcards, always free.</p>
    <p><b>GlossLink Pro — $9.99/mo or $75/yr:</b> every word in this pack, <b>every Gloss Pack</b> (all {"10"}+ textbooks, and every one we add), unlimited words, Auto-detect and PDF reading.</p>
    {pack?.free_terms?.length > 0 && (
      <p data-testid="pack-free-terms"><b>Your 20 free terms:</b> {pack.free_terms.join(", ")}.</p>
    )}
  </div>
);
